import { isEqual } from "lodash";
import * as React from "react";

import { RegionSetQualifier, RegionSetStatus } from "@app/region-sets";
import { NewActionMenu } from "@app/shared/action-menu";

import { CountyProjectContextProps } from "@app/projects/CountyProjectContext";

import { RegionSetStatusType } from "@app/projects/enumerators";
import ProjectService from "@app/projects/projectService";
import { QESButton, QESRadioButton } from "@app/shared/qes-standards";

interface BulkStatusSelectorOnlyProps {
  regions: RegionSetQualifier[];
  setBulkStatus: (regions: RegionSetQualifier[], newStatus: RegionSetStatusType) => void;
}

interface BulkStatusSelectorState {
  error: boolean; // Occurs when a status change fails to process.
  loading: boolean; // Occurs when a status is being changed.

  activeStatus: RegionSetStatusType;
  regionStatus: RegionSetStatusType[];
  statuses: RegionSetStatus[];

  triggerHide?: () => void;
}

type BulkStatusSelectorProps = CountyProjectContextProps & BulkStatusSelectorOnlyProps;

export default class BulkStatusSelector extends React.Component<BulkStatusSelectorProps, BulkStatusSelectorState> {
  private static readonly defaultValues = [
    RegionSetStatusType.Active,
    RegionSetStatusType.Pending,
    RegionSetStatusType.Approved,
    RegionSetStatusType.Opportunity,
  ];
  private projectService = new ProjectService();

  constructor(props: BulkStatusSelectorProps) {
    super(props);

    this.state = {
      activeStatus: RegionSetStatusType.Unknown,
      error: false,
      loading: false,
      regionStatus: [],
      statuses: null,
    };
  }

  public async componentDidUpdate(prevProps: BulkStatusSelectorProps) {
    if (!isEqual(prevProps.regions, this.props.regions)) {
      const statuses = await this.projectService.getStatuses(this.props.project.projectId, this.props.regions.map((r) => r.regionSetId), this.props.runConfiguration.projectRunConfigurationId);
      this.setState({
        statuses,
      });
    }
  }

  public render() {
    const menu = (
      <div className="container-fluid action-menu-wrapper status-selector">
        <i className="col-12">
          Current Status: {this.state.statuses && this.state.statuses.length > 0 && this.state.statuses.every((s) => s.status === this.state.statuses[0].status) ?
            this.state.statuses[0].status === "Unknown" ?
              "Active" :
              this.state.statuses[0].status :
            "Multiple"}
        </i>
        <div className="qes-radio-group__container pl-3">
          {BulkStatusSelector.defaultValues.map((defaultValue) => (
            <QESRadioButton
              key={defaultValue}
              label={defaultValue}
              onLabelClick={(e) => this.handleActiveStatusChange(e, defaultValue)}
              name="county-status"
              checked={this.isSelected(defaultValue, this.state.activeStatus)}
              readOnly={true}
              onClick={(e) => this.handleActiveStatusChange(e, defaultValue)}
              className="qes-radio"
              testId={`bulk-status-selector--${defaultValue}`}
            />
          ))}
        </div>
        <div className="col-12 status-selector-row status-selector-buttons">
          <QESButton qesTestId="bulk-status-selector_update" qesType="primary" onClick={this.handleUpdateClick} disabled={this.state.activeStatus === RegionSetStatusType.Unknown}>Update</QESButton>
        </div>
      </div>
    );

    if (this.props.regions.length > 0) {
      return (
        <NewActionMenu
          buttonClassNames="font-weight-normal text-dark"
          buttonElement={<><i className="pl-10 fa fa-pencil-square-o" /> Change Status</>}
          buttonText="Change Status"
          testId="bulk-status-selector"
          fixMenu={true}
          hideArrow={true}
        >
          {menu}
        </NewActionMenu>
      );
    } else {
      return (
        <span className="ml-3" title="This option will enable when one or more regions is selected"><i className="pl-10 fa fa-pencil-square-o" /> Change Status</span>
      );
    }
  }

  private handleActiveStatusChange = (e: React.MouseEvent, status: RegionSetStatusType) => {
    e.stopPropagation();
    this.setState({
      activeStatus: status,
    });
  }

  private handleUpdateClick = async () => {
    if (this.props.regions.length > 0) {
      await this.props.setBulkStatus(this.props.regions, this.state.activeStatus);
    }
  }

  private isSelected = (defaultValue: RegionSetStatusType, activeStatus: RegionSetStatusType) => {
    return defaultValue === activeStatus;
  }
}
